import { Box } from '@material-ui/core';
import React, { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { Button, Paper } from '@mui/material';
import { githubAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { useConfluenceOrganizationUsers } from '../hook/useConfluenceOrganizationUsers';
import { useTeamMemberStates } from '../hook/useTeamMemberStates';
import { useOrgUsers } from '../hook/useOrgUsers';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Progress } from '@backstage/core-components';
import { AlertSnackbar, AppContext, ConfluenceItem } from '@softwareone/plugin-project-management-react';
import { useUserProfile } from '@backstage/plugin-user-settings';
import {
  addTeamMembers,
  extractGroupAdminInfo,
  processProjectTeamInfo,
  TeamResponse,
  UserObject,
} from '../utils/utils';
import { ConfluenceUserData, SystemEntity } from '@softwareone/plugin-project-management-common';

export const ConfluenceSpace = () => {
  const { administrators, setAdministrators, contributors, setContributors, customers, setCustomers } =
    useTeamMemberStates();

  const [allUsers, setAllUsers] = useState<ConfluenceUserData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOwner, setIsOwner] = useState<boolean | undefined>(false);
  const [users, setUsers] = useState<string[]>([]);
  const [status, setStatus] = useState<'success' | 'error'>('success');
  const [message, setMessage] = useState('');
  const [open, setOpen] = useState(false);
  const hasFetchedTeamMembers = useRef(false);
  const hasFetchedProjectTeam = useRef(false);
  const { entity } = useEntity();
  const {
    jiraProjectKey,
    confluenceSpaceKey,
    name: currentSystemName,
    customer,
    title,
    creatorActiveDirectoryId,
  } = entity.metadata as SystemEntity;

  let initialValueConfluenceVisibleForSwoEmployees: boolean | undefined;
  if (typeof entity.metadata?.confluenceVisibilityForSwoEmployees === 'boolean') {
    initialValueConfluenceVisibleForSwoEmployees = entity.metadata.confluenceVisibilityForSwoEmployees;
  } else if (typeof entity.metadata?.everyoneIsRepoReader === 'boolean') {
    initialValueConfluenceVisibleForSwoEmployees = entity.metadata.everyoneIsRepoReader;
  } else {
    initialValueConfluenceVisibleForSwoEmployees = undefined;
  }

  const [confluenceVisibilityForSwoEmployees, setConfluenceVisibilityForSwoEmployees] = useState<boolean | undefined>(
    initialValueConfluenceVisibleForSwoEmployees
  );
  const [isFormValid, setIsFormValid] = useState(!!jiraProjectKey);
  const [spaceKey, setSpaceKey] = useState<string>((jiraProjectKey as string) || '');
  const [prevUsers, setPrevUsers] = useState<{
    gitHubContributors: { login: string }[];
    gitHubReaders: { login: string }[];
    gitHubOwners: { login: string }[];
  }>();
  const [dispatchLoading, setDispatchLoading] = useState<boolean>(false);
  const {
    services: { confluenceService, projectService },
  } = useContext(AppContext);
  const gitHubApi = useApi(githubAuthApiRef);
  const { value, error } = useConfluenceOrganizationUsers();
  const { prevUsersLoading, getEmailsUserNames } = useOrgUsers(projectService);
  const { backstageIdentity, profile } = useUserProfile();
  const formattedProfile = { id: '', authProvider: '', ...profile };
  const projectKey = currentSystemName;

  // Extract the second part of projectKey (after the dash) for Confluence validation exception
  const projectKeyParts = projectKey.split('-');
  const projectKeyIdentifier = projectKeyParts.length > 1 ? projectKeyParts[1] : undefined;

  const generateSpaceName = () => `${customer}-${title}`;

  const fetchTeamMembers = useCallback(async () => {
    if (hasFetchedTeamMembers.current || !confluenceSpaceKey) {
      return;
    }

    hasFetchedTeamMembers.current = true;
    type usersResponse = {
      data: {
        groupName: string;
        users: ConfluenceUserData[];
      }[];
    };

    try {
      const teamMembers = await confluenceService.getTeamMembers(projectKey);
      const json = teamMembers as usersResponse;
      json.data.forEach(group => {
        if (group.groupName.includes('administrators')) {
          setAdministrators(group.users.map(user => user.publicName));
        }
        if (group.groupName.includes('contributors')) {
          setContributors(group.users.map(user => user.publicName));
        }
        if (group.groupName.includes('customers')) {
          setCustomers(group.users.map(user => user.publicName));
        }
      });
      setIsLoading(false);
    } catch (err) {
      const errorMessage = err instanceof Response ? err.statusText : 'Unknown error occurred';
      setMessage(errorMessage);
      setStatus('error');
      setOpen(true);
      setIsLoading(false);
    }
  }, [confluenceSpaceKey, projectKey, confluenceService]);

  useEffect(() => {
    if (error) {
      setIsLoading(false);
      setStatus('error');
      setMessage(error.message);
      setOpen(true);
      return;
    }
    if (!value) return;

    setAllUsers((value as ConfluenceUserData[]).map(val => val));
    setUsers((value as { publicName: string }[]).map(val => val.publicName));

    if (!confluenceSpaceKey) {
      setIsLoading(false);
      return;
    }

    setIsFormValid(true);
    fetchTeamMembers();
  }, [value, error, confluenceSpaceKey, fetchTeamMembers]);

  const userObject: UserObject = {
    type: backstageIdentity?.type || '',
    userEntityRef: backstageIdentity?.userEntityRef || '',
    ownershipEntityRefs: backstageIdentity?.ownershipEntityRefs || [],
  };

  const hasGithubAdmins: boolean = extractGroupAdminInfo(userObject);

  useEffect(() => {
    const isUserOwner = async () => {
      if (hasFetchedProjectTeam.current) {
        return;
      }
      hasFetchedProjectTeam.current = true;

      if (formattedProfile.authProvider === 'GitHub') {
        const profileData = await gitHubApi.getProfile();

        const teamData: TeamResponse = await processProjectTeamInfo(
          projectService,
          currentSystemName,
          hasGithubAdmins,
          setIsOwner,
          profileData
        );
        setPrevUsers({
          gitHubContributors: teamData.gitHubContributors,
          gitHubReaders: teamData.gitHubReaders,
          gitHubOwners: teamData.gitHubOwners,
        });
        return;
      }

      if (formattedProfile.id === creatorActiveDirectoryId) {
        setIsOwner(true);
        const data = await projectService.getProjectTeamInfo(projectKey);
        setPrevUsers(data);
      }
    };

    isUserOwner();
  }, [
    hasGithubAdmins,
    profile,
    formattedProfile.authProvider,
    formattedProfile.id,
    creatorActiveDirectoryId,
    gitHubApi,
    projectService,
    currentSystemName,
    projectKey,
  ]);

  const handleClose = (_event?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpen(false);
  };

  type ChangeData = {
    confluenceAdministrators: string[];
    confluenceContributors: string[];
    confluenceCustomers: string[];
    confluenceSpaceKey?: string;
    confluenceVisibilityForSwoEmployees?: boolean;
  };
  const handleChange = (e: ChangeData) => {
    setAdministrators(e.confluenceAdministrators);
    setContributors(e.confluenceContributors);
    setCustomers(e.confluenceCustomers);
    setSpaceKey(e.confluenceSpaceKey || '');
    setConfluenceVisibilityForSwoEmployees(e.confluenceVisibilityForSwoEmployees);
  };
  const handleValidityChange = (e: boolean) => {
    setIsFormValid(e);
  };

  const formatUsers = () => {
    return {
      confluenceAdministrators: allUsers
        .filter(user => administrators.includes(user.publicName))
        .map(user => user.accountId),
      confluenceContributors: allUsers
        .filter(user => contributors.includes(user.publicName))
        .map(user => user.accountId),
      confluenceCustomers: allUsers.filter(user => customers.includes(user.publicName)).map(user => user.accountId),
    };
  };

  const handleUpdateSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault();
    setIsLoading(true);
    try {
      const updateTeamMembers = {
        projectKey,
        ...formatUsers(),
        confluenceVisibilityForSwoEmployees: confluenceVisibilityForSwoEmployees,
      };
      await confluenceService.updateTeamMembers(updateTeamMembers);
      setIsLoading(false);
      setMessage('Users are successfully assigned');
      setStatus('success');
      setOpen(true);
    } catch (err) {
      console.error('Error:', err);
      setMessage('An error occurred during the Confluence space update');
      setStatus('error');
      setIsLoading(false);
      setOpen(true);
    }
  };
  const handleCreateSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault();
    setIsLoading(true);
    try {
      const createConfluenceSpace = {
        confluenceVisibilityForSwoEmployees: !!confluenceVisibilityForSwoEmployees,
        projectKey,
        confluenceSpaceKey: spaceKey,
        ...formatUsers(),
      };
      await confluenceService.createConfluenceSpace(createConfluenceSpace);
      setIsLoading(false);
      setMessage('Users are successfully assigned. It takes time to be in sync with SoftwareOne DevOps platform');
      setStatus('success');
      setOpen(true);
    } catch (err) {
      console.error('Error:', err);
      setMessage('An error occurred during the Confluence space creation');
      setStatus('error');
      setIsLoading(false);
      setOpen(true);
    }
  };

  const performActions = async () => {
    setDispatchLoading(true);
    const userEmail = await getEmailsUserNames();
    if (!prevUsersLoading) {
      addTeamMembers(userEmail, allUsers, prevUsers, setAdministrators, setContributors, setCustomers);
    }
    setDispatchLoading(false);
  };

  if (isLoading || dispatchLoading) {
    return <Progress />;
  }

  if (!confluenceSpaceKey) {
    return (
      <>
        <form onSubmit={handleCreateSubmit}>
          <Box>
            <Paper
              sx={{
                display: 'flex',
                flexDirection: 'column',
                m: 1,
              }}
            >
              <ConfluenceItem
                confluenceSpaceName={generateSpaceName()}
                confluenceSpaceKey={spaceKey}
                confluenceAdministrators={administrators}
                confluenceContributors={contributors}
                confluenceCustomers={customers}
                withSharedJiraProject={!!jiraProjectKey}
                confluenceVisibilityForSwoEmployees={confluenceVisibilityForSwoEmployees}
                onChange={handleChange}
                onValidityChange={handleValidityChange}
                isDisabled={!isOwner}
                userList={users}
                getTeamMembersForConfluence={performActions}
                allowedJiraKeyException={jiraProjectKey}
                allowedProjectKeyException={projectKeyIdentifier}
              />
              <Button sx={{ m: '10px' }} variant="outlined" type="submit" disabled={!isFormValid || !isOwner}>
                Create
              </Button>
            </Paper>
          </Box>
        </form>
        <AlertSnackbar open={open} onClose={handleClose} severity={status} message={message} />
      </>
    );
  }

  return (
    <>
      <form onSubmit={handleUpdateSubmit}>
        <Box>
          <Paper
            sx={{
              display: 'flex',
              flexDirection: 'column',
              m: 1,
            }}
          >
            <ConfluenceItem
              confluenceAdministrators={administrators}
              confluenceContributors={contributors}
              confluenceCustomers={customers}
              userList={users}
              enableSpaceInfo={false}
              isDisabled={!isOwner}
              onChange={handleChange}
              getTeamMembersForConfluence={performActions}
              confluenceVisibilityForSwoEmployees={confluenceVisibilityForSwoEmployees}
              onValidityChange={handleValidityChange}
            />
            <Button sx={{ m: '10px' }} variant="outlined" type="submit" disabled={!isFormValid || !isOwner}>
              Save
            </Button>
          </Paper>
        </Box>
      </form>
      <AlertSnackbar open={open} onClose={handleClose} severity={status} message={message} />
    </>
  );
};
